/** @jsxImportSource @emotion/react */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Empty, Input, Spin, Upload, message } from 'antd';
import { DownOutlined, PictureOutlined } from '@ant-design/icons';
import type { UploadChangeParam, UploadFile } from 'antd/es/upload';
import moment from 'moment';
import groupBy from 'lodash/groupBy';
import sortBy from 'lodash/sortBy';
import EventSourcePolyfill from 'eventsource-polyfill';
import { useChatContext } from '@/shared/contexts/chatContext';
import { useMemberContext } from '@/shared/contexts/memberContext';
import { getMemberId } from '@/shared/helpers/userCred';
import APIUtil from '@/shared/helpers/api-utils';
import careRoomApi from '@/apis/lib/care-room';
import careMessageApi, { CareMessageType } from '@/apis/lib/care-message';
import { removeAll } from '@/apis/lib/user-notification';
import { playChatNotificationSound } from '@/shared/helpers/audio';
import AdminChatSvg from '../FloatingChatroomAccess/AdminChatSvg';
import * as styles from './styles';

const uploaderInfo = APIUtil.getUploaderInfo();

interface GroupedCareMessage {
    day: string;
    times: CareMessageType[];
}

const FloatingCareroom: React.FC = () => {
    const { chat, closeFloatingCareroom } = useChatContext();
    const { member } = useMemberContext();

    const [careRoom, setCareRoom] = useState<{ id: number } | null>(null);
    const [careMessages, setCareMessages] = useState<GroupedCareMessage[]>([]);
    const [onLoadingCareMessage, setOnLoadingCareMessage] = useState(false);
    const [onLoadingSendMessage, setOnLoadingSendMessage] = useState(false);
    const [chatTextAreaValue, setChatTextAreaValue] = useState('');
    const [chatImageUrls, setChatImageUrls] = useState<{ name?: string; url?: string } | null>(
        null
    );
    const [textAreaRow, setTextAreaRow] = useState(1);

    const chatContainerRef = useRef<HTMLDivElement>(null);
    const eventSourceRef = useRef<EventSourcePolyfill | null>(null);
    const careRoomRef = useRef<{ id: number } | null>(null);

    const memberId = useMemo(() => {
        if (member?.id) return member.id;
        return getMemberId();
    }, [member]);

    const isNoMessage = careMessages.length === 0;

    const scrollToBottom = () => {
        setTimeout(() => {
            if (chatContainerRef.current) {
                chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
            }
        }, 100);
    };

    const checkIsMyCareMessage = (careMessage: CareMessageType) => !careMessage.adminId;

    const runMultipleAck = useCallback(async () => {
        if (!memberId) return;
        await removeAll({
            memberId,
            topicKey: 'from_admin.care_message.received_by_[USER_ID].*',
        });
    }, [memberId]);

    const fetchCareMessages = useCallback(async () => {
        if (!careRoomRef.current?.id || !memberId) return;

        setOnLoadingCareMessage(true);
        try {
            const { data } = await careMessageApi.index({
                'findBy-careRoomId': `eq,careRoomId,${careRoomRef.current.id},number`,
                'findBy-memberId': `eq,memberId,${memberId},number`,
            });

            const rows: CareMessageType[] = data?.data || [];
            const groups = groupBy(rows, (careMessage: CareMessageType) =>
                moment(careMessage.createdAt).startOf('day').format()
            );

            const groupingResult = Object.keys(groups).map(day => ({
                day,
                times: sortBy(groups[day], 'createdAt'),
            }));

            setCareMessages(sortBy(groupingResult, 'day'));
            scrollToBottom();
        } catch (err) {
            console.error('FloatingCareroom: fetchCareMessages failed', err);
            message.error('Gagal memuat pesan admin');
        } finally {
            setOnLoadingCareMessage(false);
        }
    }, [memberId]);

    const getOrCreateCareRoom = useCallback(async () => {
        const { data } = await careRoomApi.createRoom();
        setCareRoom(data);
        careRoomRef.current = data;
        return data;
    }, []);

    const sendCareMessage = useCallback(async () => {
        if (!chatTextAreaValue && !chatImageUrls) return;
        if (!careRoomRef.current?.id || !memberId) return;
        if (onLoadingCareMessage || onLoadingSendMessage) return;

        setOnLoadingSendMessage(true);
        try {
            const { data: newCareMessage } = await careMessageApi.sendMessage({
                message: chatTextAreaValue || ' ',
                memberId,
                careRoomId: careRoomRef.current.id,
                imageUrls: chatImageUrls,
            });

            setChatTextAreaValue('');
            setChatImageUrls(null);
            setTextAreaRow(1);

            setCareMessages(prev => {
                if (prev.length === 0) {
                    return [
                        {
                            day: moment(newCareMessage.createdAt).startOf('day').format(),
                            times: [newCareMessage],
                        },
                    ];
                }

                const next = [...prev];
                const last = { ...next[next.length - 1], times: [...next[next.length - 1].times] };
                last.times.push(newCareMessage);
                next[next.length - 1] = last;
                return next;
            });

            scrollToBottom();
        } catch (err) {
            console.error('FloatingCareroom: sendCareMessage failed', err);
            message.error('Gagal mengirim pesan');
        } finally {
            setOnLoadingSendMessage(false);
        }
    }, [chatTextAreaValue, chatImageUrls, memberId, onLoadingCareMessage, onLoadingSendMessage]);

    const handleImageUploaderChanged = (info: UploadChangeParam) => {
        if (info.file.status === 'done') {
            const response = info.file.response;
            const path = Array.isArray(response) ? response[0]?.path : response?.path;
            if (!path) {
                message.error('Upload gagal');
                return;
            }

            setChatImageUrls({
                name: info.file.name,
                url: path,
            });
            setChatTextAreaValue(prev => prev || ' ');
        } else if (info.file.status === 'error') {
            message.error(`${info.file.name} upload failed`);
        }
    };

    useEffect(() => {
        if (chatImageUrls) {
            sendCareMessage();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chatImageUrls]);

    const beforeUploadDoc = (file: UploadFile) => {
        if (!file.size) return false;
        const isLt12M = file.size / 1024 / 1024 < 12;
        if (!isLt12M) {
            message.error('File must smaller than 12MB');
        }
        return isLt12M;
    };

    const handleKeyDownEvent = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            sendCareMessage();
        }
        if (event.key === 'Enter' && event.shiftKey && textAreaRow <= 3) {
            setTextAreaRow(prev => prev + 1);
        }
    };

    const handlingNewChatMessageEvent = useCallback(async () => {
        await fetchCareMessages();
        playChatNotificationSound();
        if (chat.isCareRoomOpen) {
            runMultipleAck();
        }
    }, [fetchCareMessages, chat.isCareRoomOpen, runMultipleAck]);

    const listenOnNewChatMessage = useCallback(() => {
        if (eventSourceRef.current || !memberId) return;

        const accessToken = localStorage.getItem('accessToken');
        if (!accessToken) return;

        const topicsStr = `topics[]=${encodeURIComponent(
            `from_admin.care_message.received_by_${memberId}.*`
        )}`;

        const xhrHeaders = {
            Authorization: `Bearer ${accessToken}`,
            Accept: 'text/event-stream',
            'Cache-Control': 'no-cache',
            'X-Requested-With': 'XMLHttpRequest',
        };

        const es = new EventSourcePolyfill(
            `${process.env.REACT_APP_API_URL}/api/v2/notifications/listen?${topicsStr}`,
            {
                xhrHeaders,
                withCredentials: false,
                bufferSizeLimit: 16 * 1024 * 1024,
            } as any
        );

        es.addEventListener('ping', () => {});
        es.addEventListener('signal', handlingNewChatMessageEvent);
        eventSourceRef.current = es;
    }, [memberId, handlingNewChatMessageEvent]);

    useEffect(() => {
        if (!chat.isCareRoomOpen) {
            return;
        }

        let cancelled = false;

        (async () => {
            try {
                await getOrCreateCareRoom();
                if (cancelled) return;
                await fetchCareMessages();
                await runMultipleAck();
                listenOnNewChatMessage();
            } catch (err) {
                console.error('FloatingCareroom: init failed', err);
                message.error('Gagal membuka chat admin');
            }
        })();

        return () => {
            cancelled = true;
            if (eventSourceRef.current) {
                try {
                    eventSourceRef.current.close();
                } catch (e) {
                    // ignore
                }
                eventSourceRef.current = null;
            }
        };
    }, [
        chat.isCareRoomOpen,
        getOrCreateCareRoom,
        fetchCareMessages,
        runMultipleAck,
        listenOnNewChatMessage,
    ]);

    if (!chat.isCareRoomOpen) {
        return null;
    }

    return (
        <div css={styles.floatingCareroom}>
            <div css={styles.floatingWrapper}>
                <div css={styles.chatroomContainer}>
                    <div css={styles.topist}>
                        <div css={styles.chatUserWrapper}>
                            <AdminChatSvg style={{ fontSize: 36, width: 36, height: 36 }} />
                            <div className="user-info">
                                <div className="user-name">
                                    <p>Admin Propertigrab</p>
                                </div>
                                <p>Ada yang bisa kami bantu?</p>
                            </div>
                        </div>
                        <div css={styles.closeWrapper} onClick={closeFloatingCareroom}>
                            <DownOutlined />
                        </div>
                    </div>

                    <div css={styles.chatroom} ref={chatContainerRef}>
                        {onLoadingCareMessage ? (
                            <div css={styles.centerSpin}>
                                <Spin />
                            </div>
                        ) : isNoMessage ? (
                            <div css={styles.emptyWrapper}>
                                <Empty
                                    description={
                                        <span>
                                            Belum ada pesan.
                                            <br />
                                            Mulai chat dengan admin.
                                        </span>
                                    }
                                />
                            </div>
                        ) : (
                            careMessages.map(group => (
                                <div key={`care-day-${group.day}`}>
                                    <div css={styles.dayBreak}>
                                        <span>{moment(group.day).format('DD MMM YYYY')}</span>
                                    </div>
                                    {group.times.map(cm => (
                                        <div
                                            key={`care-msg-${cm.id}`}
                                            css={styles.chatItem}
                                            className={
                                                checkIsMyCareMessage(cm) ? 'mine' : 'theirs'
                                            }
                                        >
                                            <div css={styles.chatBubble}>
                                                {cm.imageUrls?.url ? (
                                                    <img
                                                        className="message-image"
                                                        src={cm.imageUrls.url}
                                                        alt={cm.imageUrls.name || 'attachment'}
                                                        onClick={() =>
                                                            window.open(cm.imageUrls?.url, '_blank')
                                                        }
                                                    />
                                                ) : null}
                                                {cm.message?.trim() ? (
                                                    <p className="message-text">{cm.message}</p>
                                                ) : null}
                                                <span className="time">
                                                    {moment(cm.createdAt).format('HH:mm')}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ))
                        )}
                    </div>

                    <div css={styles.composer}>
                        <Upload
                            name="file"
                            showUploadList={false}
                            action={uploaderInfo.url}
                            headers={uploaderInfo.headers}
                            beforeUpload={beforeUploadDoc as any}
                            onChange={handleImageUploaderChanged}
                        >
                            <span css={styles.uploadBtn}>
                                <PictureOutlined />
                            </span>
                        </Upload>
                        <Input.TextArea
                            value={chatTextAreaValue}
                            onChange={e => setChatTextAreaValue(e.target.value)}
                            onKeyDown={handleKeyDownEvent}
                            autoSize={{ minRows: 1, maxRows: Math.max(textAreaRow, 1) }}
                            placeholder="Tulis pesan..."
                            disabled={!careRoom || onLoadingSendMessage}
                            style={{ flex: 1 }}
                        />
                        <button
                            type="button"
                            css={styles.sendBtn}
                            disabled={
                                !careRoom ||
                                onLoadingSendMessage ||
                                (!chatTextAreaValue.trim() && !chatImageUrls)
                            }
                            onClick={sendCareMessage}
                        >
                            Kirim
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FloatingCareroom;
